/**
 * Google Pay Button — Web
 *
 * Creates a Google Pay button via PaymentsClient.createButton() and wires
 * click → loadPaymentData → onPaymentAuthorized. Idempotent on re-renders.
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';

import theme from '../../../../../style/theme';

import type {GooglePayButtonWebProps} from './googlePayButtonTypes';
import {googlePayService} from './googlePayService';

export function GooglePayButton({
  serviceId,
  remainingBalanceCents,
  onPaymentAuthorized,
  onCancel,
  onError,
  isReady,
  isSupported,
  client,
  error,
  buttonType = 'pay',
  buttonColor = 'black',
  disabled = false,
}: GooglePayButtonWebProps) {
  // Readiness is supplied by the probe owned by CheckoutActionButton.web
  // (L2-01: this button no longer runs its own probe). The parent renders the PAY
  // NOW card fallback on settled-not-ready / error, so the button never strands
  // the checkout with a blank action area.
  const isBusyRef = useRef(false);
  // Use state to hold the container node so createButton fires when the node mounts
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const handleClick = useCallback(async () => {
    if (disabled || isBusyRef.current || !client) return;
    isBusyRef.current = true;

    try {
      const request = await googlePayService.getPaymentRequest(serviceId, remainingBalanceCents);
      const payment = await client.loadPaymentData(request);
      await onPaymentAuthorized(payment);
    } catch (err) {
      const statusCode = (err as {statusCode?: string}).statusCode;
      if (statusCode === 'CANCELED') {
        onCancel?.();
      } else {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      isBusyRef.current = false;
    }
  }, [client, disabled, remainingBalanceCents, serviceId, onPaymentAuthorized, onCancel, onError]);

  // Keep the latest click handler in a ref so the Google button is created ONCE
  // per readiness rather than torn down + recreated on every parent re-render.
  // The config factory allocates fresh handler closures each render, so without
  // this the createButton/replaceChildren effect would re-fire on any observed
  // store change (e.g. a MobX cart update) and recreate the button DOM.
  const handleClickRef = useRef(handleClick);
  useEffect(() => {
    handleClickRef.current = handleClick;
  }, [handleClick]);

  useEffect(() => {
    if (!isReady || !client || !container) return;

    const button = client.createButton({
      onClick: () => handleClickRef.current(),
      buttonType,
      buttonColor,
      buttonSizeMode: 'fill',
    });
    container.replaceChildren(button); // safe clear + append (no innerHTML)
  }, [isReady, client, container, buttonType, buttonColor]);

  if (error) return null;

  // Probe settled (isReadyToPay resolved) but this client cannot pay: render
  // nothing instead of an indefinite spinner — mirrors ApplePayButton.web's
  // terminal not-supported branch. The selector already gated the tile on its
  // own probe, so a divergent settled-not-ready here is transient unavailability;
  // returning null avoids a permanent stuck spinner at the checkout action.
  if (isSupported && !isReady) return null;

  if (!isReady) {
    // Probe still in flight (isReadyToPay not yet resolved).
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={theme.colour.uiControls?.primary ?? '#0054A6'} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* div callback-ref triggers createButton when node mounts */}
      <div ref={setContainer} style={{width: '100%'}} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {width: '100%'},
  loadingContainer: {padding: 12, alignItems: 'center'},
});

export default GooglePayButton;
